import * as React from "react";
import styled, { css } from "../../../styled/index";
import lighten from "polished/lib/color/lighten";
import { CS } from "../../../styles/index";
import { BaseInputCSS } from "../input/index";
import { FaChevronDown } from "react-icons/fa";
import shortid from "shortid";
import {RotateLoader} from "../../loaders/rotate";
const ClickOutComponent = require("react-onclickout");

interface WrapperProps {
  shadow?: "one" | "two" | "three";
  focused: boolean;
}

export const Wrapper = styled("div")<WrapperProps>`
  ${BaseInputCSS};
  ${(props) => props.shadow ? CS.shadow.light[props.shadow] : ""};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  border-color: ${({focused, theme}) => focused ? theme.colors.primary : theme.input.border};
  padding: 0;
`;

export const WrapperInner = styled("div")<{ type: "single" | "multi"; }>`
  display: flex;
  flex-grow: 1;
  height: auto;
  padding: 3px;
  ${({type}) => type === "single" ? css`
    align-items: center;
    flex-wrap: nowrap;
  ` : css`
    flex-wrap: wrap;
  `};
  > * {
    margin: 3px;
  }
`;

export const InputField = styled("input")`
  ${BaseInputCSS};
  cursor: default;
  border: none;
  width: auto;
  flex-grow: 1;
  height: 24px;
  line-height: normal;
  padding: 0 5px;
  &:focus {
    border: none !important;
  }
`;

export const InputIcon = styled("div")`
  height: 22px;
  min-width: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid ${({theme}) => theme.input.border};
  color: ${({theme}) => theme.input.border};
  font-size: 12px;
`;

export const Dropdown = styled("div")<{ active: boolean }>`
  ${CS.shadow.light.two};
  z-index: 3;
  position: absolute;
  top: 120%;
  width: 100%;
  height: auto;
  max-height: 200px;
  overflow-y: auto;
  display: ${({active}) => active ? "block" : "none"};
  border-radius: 3px;
  background: ${({theme}) => theme.input.background};
  color: ${({theme}) => theme.input.text};
  border: 1px solid ${({theme}) => theme.input.border};
`;

export const DropdownOption = styled("div")<{ active: boolean }>`
  cursor: pointer;
  width: 100%;
  padding: 8px 12px;
  ${({active}) => active ? css`
    background: ${({theme}) => lighten(0.07, theme.colors.primary)};
    color: ${({theme}) => theme.colors.primary_text};
  ` : ""};
  &:hover {
    background: ${({theme}) => theme.colors.primary};
    color: ${({theme}) => theme.colors.primary_text};
  }
`;

type Option<T> = T & { label: string };

interface State<T> {
  options: Array<Option<T>>;
  value: Option<T> | null;
  input: string;
  focused: boolean;
  loading: boolean;
}

interface Props<T> {
  id?: string;
  placeholder?: string;
  shadow?: "one" | "two" | "three";
  fetch: (search: string) => Promise<Array<Option<T>>>;
  onChange: (option: Option<T>) => void;
  value: string;
}

export class SelectAsync<T> extends React.PureComponent<Props<T>, State<T>> {

  inputId: string;
  lastOpened: number | null = null;
  timer: any;

  constructor(props: Props<T>) {
    super(props);
    this.state = {
      options: [],
      value: null,
      input: "",
      focused: false,
      loading: true,
    };
    this.inputId = props.id || shortid.generate();
  }

  componentDidUpdate() {
    this.lastOpened = this.state.focused ? Date.now() : null;
  }

  onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    if (this.state.focused) {

      const input = e.target.value;

      this.setState({ input, options: [], loading: true });

      clearTimeout(this.timer);

      this.timer = setTimeout(async () => {
        const options = await this.props.fetch(input);
        this.setState({ options, loading: false });
      }, 1000);

    }

  }

  onChange = (value: Option<T>) => {
    this.setState({ value });
    this.toggleDropdown(false);
    this.props.onChange(value);
  }

  onClickOut = () => {
    if (this.lastOpened && ((Date.now() - this.lastOpened) > 100)) {
      this.toggleDropdown(false);
    }
  }

  toggleDropdown = (open: boolean) => {
    const el = document.getElementById(this.inputId);
    if (open) {
      this.setState({ focused: true });
      if (el) {
        el.focus();
      }
    }
    else {

      clearTimeout(this.timer);

      this.setState({
        focused: false,
        input:  "",
        options: [],
        loading: false,
      });

      if (el) {
        el.blur();
      }

    }
  }

  render() {

    const { shadow, placeholder } = this.props;

    const { input, focused, value, options, loading } = this.state;

    const finalPlaceholder = placeholder || "Start typing to search...";

    let dropdownActive = focused;

    let inputFieldValue = "";

    if (focused) {
      inputFieldValue = input;
    }
    else if (this.props.value) {
      inputFieldValue = this.props.value;
    }

    if (dropdownActive && !input) {
      dropdownActive = false;
    }

    return (
      <Wrapper
        shadow={shadow}
        focused={focused}
        onClick={() => this.toggleDropdown(!focused)}>
        <WrapperInner type="single">
          <InputField
            id={this.inputId}
            form="doesnt-exists"
            placeholder={finalPlaceholder}
            value={inputFieldValue}
            onChange={this.onInputChange}/>
        </WrapperInner>
        <InputIcon>
          <FaChevronDown/>
        </InputIcon>
        <ClickOutComponent onClickOut={this.onClickOut}>
          <Dropdown
            active={dropdownActive}
            onClick={(e) => e.stopPropagation()}>
            {options.map((o, i) => (
              <DropdownOption key={i} onClick={() => this.onChange(o)} active={!!(value && value.label === o.label)}>
                {o.label}
              </DropdownOption>
            ))}
            {options.length === 0 && (
              <div className="text-center p-tb-2">
                {(!loading) ? "No options found" : <RotateLoader size={2}/>}
              </div>
            )}
          </Dropdown>
        </ClickOutComponent>
      </Wrapper>
    );
  }

}
